import areEqual from "fbjs/lib/areEqual";

import { IModel, Model } from "../Core/ModelStoring/Model";
import {
    isChangeModelEventInfo,
    isChangeViewModelEvent,
    isRemoveModelEventInfo,
    ModelEvent,
} from "../Core/EventManagement/ModelEvents/ModelEvent";
import { IterUtils } from "../../../../Common/IterableUtils";
import { FormSettings, getFormRootModelPath } from "../../Forms/Types/FormSettings";
import { ServerChangeSet } from "../../../../Common/CandyApiClient/Types/ChangeSet/ServerChangeSet";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsoluteResolvedFromTokens,
    PathTokens,
} from "../../../../Common/ModelPath/ModelPath";
import {
    ICandyApiDraftClient,
    IInnerChangeResult,
    PathLoadSpecification,
} from "../../../../Common/CandyApiClient/ICandyApiClient";
import { loadInner } from "../../Forms/FormInitialization/DataFetching";
import { createActionQueue } from "../Core/Calculator2/Calculator2Compatibility";
import { ModelNormalizer } from "../Core/Normalization/ModelNormalizer";
import { DataDeclarationHelper } from "../Core/DataDeclaration/DataDeclarationHelper";
import { ICalculator } from "../Core/AutoCalculations/Calculator";
import { ICalculator2 } from "../Core/Calculator2/Calculator2";
import { ICalculationFunction } from "../Core/Calculator2/ICalculationFunction";
import { LegacyValidationFunctionAdapter } from "../Core/Calculator2/Validations/LegacyValidationFunctionAdapter";
import { AggregatedValidationFunction } from "../Core/Calculator2/AggregatedValidationFunction";
import { ValidationFunction } from "../Core/Calculator2/Validations/ValidationFunction";
import { AbsoluteModelPathSet } from "../../../../Common/ModelPath/Set/AbsoluteModelPathSet";
import { wrapDecimalOperand, wrapDecimalOrUndefined } from "../../Helpers/AutocalcCommonFunctions";
import { FormStore } from "../Core/Store/CandyStore";
import { IChangeSetManager } from "../Core/ChangeSet/IChangeSetManager";
import { MaskResolver } from "../Core/PathUtils/MaskResolver";
import { Sections } from "../Core/ModelStoring/Sections";
import { SectionNames } from "../../../../Common/CommonConstants/SectionNames";
import { truePredicate } from "../../../../Common/Funcs";
import { ChangeSet } from "../Core/ChangeSet/ChangeSet";
import { AbsoluteModelFieldPath } from "../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { Set2 } from "../../../../Common/ModelPath/Set/Set2";
import { ModelHunk } from "../Core/ModelStoring/ModelHunk";
import { ILazyLoadingManager } from "../Core/LazyLoading/LazyLoadingManager";
import { getMask } from "../../../../Common/ModelPath/ModelPathHelper";
import { Map2 } from "../../../../Common/ModelPath/Map/Map2";
import { StringArrayKey } from "../../../../Common/StringArrayKey";
import { BindingPath, ModelValue, ViewModelFieldName } from "../Core/CoreTypes";
import { isNotNullOrUndefined } from "../../../../Common/TypingUtils";
import { CalculationTargetAttribute } from "../Core/Calculator2/CalculationTarget";
import { FilteringManager } from "../Core/Filtering/FilteringManager";
import { waitPollingTask } from "../../../../Common/CandyApiClient/Types/PollingTasks/PollingTaskWaiter";

import { CalculationAwaitingOption } from "./IForm";
import { IFormReadonlyModelAccessor } from "./IFormModelAccessor";

export interface IChangesProcessor {
    processEventsAfterUpdate(sourceModel: IModel, updateEvents: readonly ModelEvent[]): void;
    processEventsAfterFormLoad(sourceModel: IModel): Promise<void>;
    processEventsAfterLazyLoad(sourceModel: IModel): void;
    processEventsAfterInitialLoad(sourceModel: IModel, updateEvents: readonly ModelEvent[]): Promise<void>;
    processEventsAfterReplaceResources(sourceModel: IModel): Promise<void>;
    waitForPendingCalculations(calculationAwaitingOption: CalculationAwaitingOption): Promise<void>;
    revalidatePaths(paths: Iterable<AbsoluteModelPath>): Promise<void>;
    customNormalize(normalizerId: string | null, pathPrefixes: AbsoluteModelPath[]): Promise<void>;
}

type EventsSubscriptionAction = (events: readonly ModelEvent[]) => void;

export class ChangesProcessor implements IChangesProcessor {
    private readonly autoCalcQueue = createActionQueue("autocalc", 1);
    private readonly validationQueue = createActionQueue("validation", 1);
    private readonly modelNormalizer: ModelNormalizer;
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly useServerSideCalculations: boolean;
    private readonly modelOverStore: IFormReadonlyModelAccessor;
    private readonly calculator: ICalculator;
    private readonly calculator2: ICalculator2;
    public readonly store: FormStore;
    private readonly filteringManager: FilteringManager;
    private readonly lazyLoadingManager: ILazyLoadingManager;
    private readonly sections: Sections;
    private readonly formSettings: FormSettings;
    private readonly candyApiDraftClient: ICandyApiDraftClient;
    private readonly changeSet: IChangeSetManager;
    private readonly eventsTrackers: Set<EventsSubscriptionAction> = new Set<EventsSubscriptionAction>();
    private readonly serverOnlyPaths: Set2<AbsoluteModelFieldPath>;

    public constructor(
        calculator: ICalculator,
        calculator2: ICalculator2,
        store: FormStore,
        modelOverStore: IFormReadonlyModelAccessor,
        changeSet: IChangeSetManager,
        formSettings: FormSettings,
        candyApiDraftClient: ICandyApiDraftClient,
        sections: Sections,
        modelNormalizer: ModelNormalizer,
        dataDeclarationHelper: DataDeclarationHelper,
        lazyLoadingManager: ILazyLoadingManager,
        filteringManager: FilteringManager,
        useServerSideCalculations: boolean
    ) {
        this.filteringManager = filteringManager;
        this.lazyLoadingManager = lazyLoadingManager;
        this.sections = sections;
        this.formSettings = formSettings;
        this.candyApiDraftClient = candyApiDraftClient;
        this.changeSet = changeSet;
        this.calculator = calculator;
        this.calculator2 = calculator2;
        this.modelNormalizer = modelNormalizer;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.useServerSideCalculations = useServerSideCalculations;
        this.modelOverStore = modelOverStore;
        this.store = store;

        this.changeSet.onServerChangesReceived((_, queuedChangeSets: Iterable<ChangeSet>, paths) =>
            this.handleChangedPathReceived(queuedChangeSets, paths)
        );

        this.serverOnlyPaths = new Set2(
            Iterator.from(this.lazyLoadingManager.getServerOnlyRules().values())
                .map(x => (x.modelPath instanceof AbsoluteModelFieldPath ? x.modelPath : undefined))
                .filter(isNotNullOrUndefined)
        );
    }

    public async revalidatePaths(paths: AbsoluteModelPath[]): Promise<void> {
        const isRevalidationAllowed = !this.useServerSideCalculations || this.changeSet.isRedefined();
        if (isRevalidationAllowed) {
            await this.validationQueue.enqueueWithPromise(async () => {
                const model = this.createModel();
                const events = model.beginGetEvents();
                await this.calculator2.revalidateTargets(model, paths);
                this.processEventsAfterUpdate(model, events.releaseAndGetEvent());
            });
        }
    }

    public async processEventsAfterFormLoad(model: IModel): Promise<void> {
        const eventsForAutoFlags = model.beginGetEvents();
        const eventsForErrors = model.beginGetEvents();

        const allEvents = model.beginGetEvents();
        const normalizationEvent = model.beginGetEvents();
        const normalizationAndCalculatorEvents = model.beginGetEvents();
        this.modelNormalizer.normalizeSection(model, SectionNames.requisitesSection);
        this.calculator.calculateAll(model, normalizationEvent.releaseAndGetEvent());
        await this.calculator2.processEvents(model, normalizationAndCalculatorEvents.releaseAndGetEvent());

        this.updateAndApplyEvents(
            model,
            () => eventsForAutoFlags.releaseAndGetEvent(),
            () => eventsForErrors.releaseAndGetEvent(),
            () => allEvents.releaseAndGetEvent()
        );
    }

    public async processEventsAfterInitialLoad(model: IModel, updateEvents: readonly ModelEvent[]): Promise<void> {
        const eventsForAutoFlags = model.beginGetEvents();
        const eventsForErrors = model.beginGetEvents();

        const context2 = model.beginGetEvents();
        const normalizationEvents = model.beginGetEvents();
        this.modelNormalizer.normalizeSection(model, SectionNames.defaultSection);

        this.calculator.calculateAll(model, updateEvents.concat(normalizationEvents.releaseAndGetEvent()));
        await this.calculator2.calculateAll(
            model,
            truePredicate,
            calculationFunction =>
                !(
                    !!this.formSettings.doNotValidateFormOnStart &&
                    calculationFunction.getTarget().targetAttribute == CalculationTargetAttribute.Validation
                )
        );

        this.updateAndApplyEvents(
            model,
            () => IterUtils.concat(updateEvents, eventsForAutoFlags.releaseAndGetEvent()),
            () => IterUtils.concat(updateEvents, eventsForErrors.releaseAndGetEvent()),
            () => context2.releaseAndGetEvent()
        );
    }

    public processEventsAfterLazyLoad(model: IModel): void {
        const normalizationEvent = model.beginGetEvents();
        const normalizationAndCalculatorEvents = model.beginGetEvents();
        this.modelNormalizer.normalizeSection(model, SectionNames.requisitesSection);
        this.calculator.calculateAll(model, normalizationEvent.releaseAndGetEvent());

        this.processEventsAfterUpdate(model, normalizationAndCalculatorEvents.releaseAndGetEvent());
    }

    public async processEventsAfterReplaceResources(model: IModel): Promise<void> {
        const eventsForAutoFlags = model.beginGetEvents();
        const eventsForErrors = model.beginGetEvents();
        const allEvents = model.beginGetEvents();
        const normalizationEvent = model.beginGetEvents();
        this.modelNormalizer.normalizeSection(model, SectionNames.requisitesSection);
        this.calculator.calculateAll(model, normalizationEvent.releaseAndGetEvent());
        await this.calculator2.calculateAll(model, x => !this.sections.isIgnoredPathByMasks(x));

        this.updateAndApplyEvents(
            model,
            () => eventsForAutoFlags.releaseAndGetEvent(),
            () => eventsForErrors.releaseAndGetEvent(),
            () => allEvents.releaseAndGetEvent()
        );
    }

    public async waitForPendingCalculations(
        calculationAwaitingOption: CalculationAwaitingOption = CalculationAwaitingOption.Calculations
    ): Promise<void> {
        await this.autoCalcQueue.enqueueWithPromise(() => Promise.resolve());
        if (calculationAwaitingOption & CalculationAwaitingOption.CalculationsAndValidations) {
            await this.validationQueue.enqueueWithPromise(() => Promise.resolve());
        }
        if (this.useServerSideCalculations && !this.changeSet.isRedefined()) {
            await this.changeSet.send();
        }
    }

    public async customNormalize(normalizerId: string | null, pathPrefixes: AbsoluteModelPath[]): Promise<void> {
        await this.changeSet.scheduleInnerChangeAction(() =>
            this.pollCustomNormalizationAsync(normalizerId, pathPrefixes)
        );
    }

    public processEventsAfterUpdate(sourceModel: IModel, updateEvents: readonly ModelEvent[]): void {
        this.markOptionalSectionsAsIgnoredByEvents(updateEvents);

        const isRequiredToCalcOnServer = this.useServerSideCalculations && !this.changeSet.isRedefined();

        if (isRequiredToCalcOnServer) {
            const deletedAutoFlags = new Set(
                Iterator.from(updateEvents)
                    .filter(x => x.name === "autoFlag" && x.type === "remove")
                    .map(x => x.path)
            );
            const eventCollectionContext = sourceModel.beginGetEvents();
            for (const eventArgs of updateEvents) {
                if (
                    eventArgs.name === "value" &&
                    this.dataDeclarationHelper.isNodeHasAutoFlagEntry(eventArgs.path) &&
                    !deletedAutoFlags.has(eventArgs.path)
                ) {
                    const type = this.dataDeclarationHelper.getType(eventArgs.path);
                    const needCastToDecimal = type === "decimal" || type === "integer";
                    const autoValue = sourceModel.getViewModelField(eventArgs.path, "autoValue");
                    const value = eventArgs.value;
                    sourceModel.setViewModelField(
                        eventArgs.path,
                        "autoFlag",
                        isValueEqualsAutoValue(value, autoValue, needCastToDecimal)
                    );
                }
            }
            sourceModel.flushIntoStore(this.store);
            const eventsToSave = [...updateEvents, ...eventCollectionContext.releaseAndGetEvent()];
            for (const eventsTracker of this.eventsTrackers) {
                eventsTracker(eventsToSave);
            }
            this.applyChangeSetEvents(eventsToSave);
        } else {
            for (const eventsTracker of this.eventsTrackers) {
                eventsTracker(updateEvents);
            }
            this.processCalculationsAsync(sourceModel, updateEvents, true);
        }
    }

    private async handleChangedPathReceived(
        queuedChangeSets: Iterable<ChangeSet>,
        paths: AbsoluteModelFieldPath[]
    ): Promise<void> {
        const clientPaths = Iterator.from(paths)
            .filter(pathWithField => {
                const mask = getMask(pathWithField.path);
                const maskWithField = new AbsoluteModelFieldPath(mask, pathWithField.field);
                return !this.serverOnlyPaths.has(maskWithField);
            })
            .toArray();

        const fixedPaths = Iterator.from(this.mapPathsByPartiallyLoadedDataToSpecPaths(clientPaths)).toArray();
        const pathForResolving = Iterator.from(this.mapPathsByPartiallyLoadedDataToRealPath(clientPaths)).toArray();

        const loadingPathSpecs: PathLoadSpecification[] = fixedPaths.map(path => ({
            pathOrPathPrefix: path,
            skip: false,
        }));

        const eventSubscription = this.beginCollectChangeSet();
        const data = await this.loadInner(loadingPathSpecs);
        const lastChangeSet = eventSubscription.releaseAndGetChangeSet();
        for (const queuedChangeSet of queuedChangeSets) {
            lastChangeSet.prependWithChangeSet(queuedChangeSet);
        }
        const capturedChangeSet = lastChangeSet.toServerChangeSet();

        const model = this.createModel();
        const resolvedChangedPaths = this.getResolvedChangedPath(model, pathForResolving);
        this.filterChangedDataWithLocalChanges(
            data,
            capturedChangeSet,
            resolvedChangedPaths,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            (bindingPath, fieldName) => model.getViewModelField(bindingPath, fieldName)
        );
        model.setBatch(data);
        model.flushIntoStore(this.store);
        this.filteringManager.notifyWithChanges(new Set2(clientPaths));
    }

    private filterChangedDataWithLocalChanges(
        data: ModelHunk,
        capturedChangeSet: ServerChangeSet,
        resolvedChangedPaths: Iterable<AbsoluteModelFieldPath>,
        getViewModelField: (
            bindingPath: BindingPath | AbsoluteResolvedModelPath,
            fieldName: string
        ) => ModelValue | undefined
    ) {
        for (const current of resolvedChangedPaths) {
            const valueFromModel = getViewModelField(current.path as AbsoluteResolvedModelPath, current.field);
            const viewModelFromServer = data.get(current.path) ?? {};
            const valueFromServer = viewModelFromServer[current.field as ViewModelFieldName];
            const legacyPathWithField = current.toString();
            const isDataChangedLocally =
                capturedChangeSet.changed[legacyPathWithField] != undefined ||
                capturedChangeSet.removed.indexOf(legacyPathWithField) >= 0;

            if (isDataChangedLocally || areEqual(valueFromModel, valueFromServer)) {
                delete viewModelFromServer[current.field as ViewModelFieldName];
            } else {
                if (current.field != "children" && !viewModelFromServer.hasOwnProperty(current.field)) {
                    viewModelFromServer[current.field as ViewModelFieldName] = undefined;
                }
            }
            data.set(current.path, viewModelFromServer);
        }
    }

    private getResolvedChangedPath(model: IModel, paths: AbsoluteModelFieldPath[]) {
        const resolvedChangedPaths = Iterator.from(paths)
            .map(pathAndField =>
                Iterator.from(MaskResolver.resolvePath(model, pathAndField.path)).map(
                    path => new AbsoluteModelFieldPath(path, pathAndField.field)
                )
            )
            .flatMap(x => x);
        const result = new Set2(resolvedChangedPaths);
        return result.values();
    }

    private *mapPathsByPartiallyLoadedDataToRealPath(
        paths: Iterable<AbsoluteModelFieldPath>
    ): Iterable<AbsoluteModelFieldPath> {
        for (const pathWithField of paths) {
            const instancesGroups = this.lazyLoadingManager.resolvePathByLoadedInstances(pathWithField.path);
            const mask = getMask(pathWithField.path);
            const finalPaths = Iterator.from(instancesGroups).map(
                instances =>
                    new AbsoluteModelFieldPath(
                        mask.applyInstancesDirty(instances.filter(PathTokens.isInstanceToken), true),
                        pathWithField.field
                    )
            );
            yield* finalPaths;
        }
    }

    private *mapPathsByPartiallyLoadedDataToSpecPaths(
        paths: Iterable<AbsoluteModelFieldPath>
    ): Iterable<AbsoluteModelFieldPath> {
        for (const pathWithField of paths) {
            const instancesGroups = this.lazyLoadingManager.resolvePathByLoadedInstances(pathWithField);
            const mask = getMask(pathWithField.path);

            const groups = new Map2<StringArrayKey, Set<string>>();
            for (const instancesGroup of instancesGroups) {
                const instances = instancesGroup.filter(PathTokens.isInstanceToken);
                const groupKey = new StringArrayKey(instances.slice(0, instancesGroup.length - 1));

                const set = groups.getOrSet(groupKey, () => new Set());
                const lastInstance = IterUtils.last(instances);
                if (lastInstance != undefined) {
                    set.add(lastInstance);
                }
            }
            const finalPaths = Iterator.from(groups.entries()).map(([key, value]) => {
                const instances = value.values().toArray();
                return new AbsoluteModelFieldPath(
                    mask.applyInstancesDirty(
                        instances.length > 0 ? [...key.items, `[${instances.join(",")}]`] : key.items,
                        true
                    ),
                    pathWithField.field
                );
            });
            yield* finalPaths;
        }
    }

    private processCalculationsAsync(
        model: IModel,
        updateEvents: Iterable<ModelEvent>,
        saveUpdateEvents: boolean = false
    ) {
        const eventsForAutoFlags = model.beginGetEvents();
        const eventsForErrors = model.beginGetEvents();
        const eventsForSave = model.beginGetEvents();

        let currentEvents = [...updateEvents];
        const eventsForAsyncRun: ModelEvent[] = [...currentEvents];
        while (currentEvents.length > 0) {
            const eventCollectionContext = model.beginGetEvents();
            for (const eventArgs of currentEvents) {
                if (eventArgs.name === "autoValue") {
                    const autoFlag = model.getViewModelField(eventArgs.path, "autoFlag");
                    if (autoFlag) {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        model.setValue(eventArgs.path, eventArgs.value);
                    } else if (autoFlag == undefined) {
                        if (eventArgs.value !== undefined) {
                            const value = model.getViewModelField(eventArgs.path, "value");
                            model.setViewModelField(eventArgs.path, "autoFlag", value === eventArgs.value);
                        }
                    }
                }

                if (eventArgs.name != undefined) {
                    this.calculator.calculate(model, eventArgs);
                }
            }
            currentEvents = eventCollectionContext.releaseAndGetEvent();
            eventsForAsyncRun.push(...currentEvents);
        }

        const additionalEvents = model.beginGetEvents();

        const eventsForCalculator = model.beginGetEvents();

        const getEventsForCalculator2Iterator = () =>
            Iterator.from(eventsForAsyncRun).filter(
                eventArgs =>
                    (isChangeModelEventInfo(eventArgs) || isRemoveModelEventInfo(eventArgs)) &&
                    isChangeViewModelEvent(eventArgs)
            );
        const nonChildrenEventsForCalculator2 = getEventsForCalculator2Iterator().filter(
            eventArgs => eventArgs.name !== "children"
        );
        for (const eventArgs of nonChildrenEventsForCalculator2) {
            this.calculator2.processSync(model, eventArgs, onlyNonValidationFunctionsPredicate);
        }

        const childrenEventsForCalculator2 = getEventsForCalculator2Iterator().filter(
            eventArgs => eventArgs.name === "children"
        );
        for (const childrenChangeEvent of childrenEventsForCalculator2) {
            this.calculator2.processSync(model, childrenChangeEvent, onlyNonValidationFunctionsPredicate);
        }

        for (const eventArgs of eventsForCalculator.releaseAndGetEvent()) {
            if (eventArgs.name != undefined) {
                this.calculator.calculate(model, eventArgs);
            }
        }

        eventsForAsyncRun.push(...additionalEvents.releaseAndGetEvent());

        this.updateAndApplyEvents(
            model,
            () => IterUtils.concat(updateEvents, eventsForAutoFlags.releaseAndGetEvent()),
            () => IterUtils.concat(updateEvents, eventsForErrors.releaseAndGetEvent()),
            () => IterUtils.concat(saveUpdateEvents ? updateEvents : [], eventsForSave.releaseAndGetEvent())
        );

        this.validationQueue.enqueue(async () => this.processValidationsAsync(eventsForAsyncRun));
    }

    private async processValidationsAsync(eventsForAsyncRun: Iterable<ModelEvent>) {
        const model = this.createModel();
        const eventsForErrors2 = model.beginGetEvents();
        const eventsForSave2 = model.beginGetEvents();

        const eventsForCalculatorAfterValidation = model.beginGetEvents();
        const calc2Events = Iterator.from(eventsForAsyncRun)
            .filter(
                eventArgs =>
                    ((isChangeModelEventInfo(eventArgs) || isRemoveModelEventInfo(eventArgs)) &&
                        eventArgs.name === "value") ||
                    eventArgs.name === "children"
            )
            .toArray();

        for (const calc2Event of calc2Events) {
            await this.calculator2.process(model, calc2Event, onlyValidationFunctionsPredicate);
        }

        for (const eventArgs of eventsForCalculatorAfterValidation.releaseAndGetEvent()) {
            if (eventArgs.name != undefined) {
                this.calculator.calculate(model, eventArgs);
            }
        }

        this.updateAndApplyEvents(
            model,
            () => [],
            () => eventsForErrors2.releaseAndGetEvent(),
            () => eventsForSave2.releaseAndGetEvent()
        );
    }

    private async pollCustomNormalizationAsync(
        normalizerId: string | null,
        pathPrefixes: AbsoluteModelPath[]
    ): Promise<IInnerChangeResult> {
        await this.candyApiDraftClient.startCustomNormalizationTask(pathPrefixes, normalizerId ?? undefined);
        return await waitPollingTask(() => this.candyApiDraftClient.getCustomNormalizationTask());
    }

    private updateAndApplyEvents(
        model: IModel,
        getEventsForAutoFlags: () => Iterable<ModelEvent>,
        getEventsForErrors: () => Iterable<ModelEvent>,
        getEventsForSave: () => Iterable<ModelEvent>
    ) {
        updateAutoFlags(model, this.dataDeclarationHelper, getEventsForAutoFlags());
        updateErrorCount(
            this.sections,
            this.dataDeclarationHelper,
            getFormRootModelPath(this.formSettings),
            model,
            getEventsForErrors()
        );
        const eventsForSave = [...getEventsForSave()];
        model.flushIntoStore(this.store);
        this.applyChangeSetEvents(eventsForSave);
    }

    private applyChangeSetEvents(events: readonly ModelEvent[]) {
        this.changeSet.applyEvents(events);
        const changedModelFieldPaths = events.reduce((result, evt) => {
            if (evt.name != undefined) {
                const modelFieldPath = new AbsoluteModelFieldPath(evt.path, evt.name);
                result.add(modelFieldPath);
            }
            return result;
        }, new Set2<AbsoluteModelFieldPath>());
        this.filteringManager.notifyWithChanges(changedModelFieldPaths);
    }

    private loadInner(loadingPathSpecs: readonly PathLoadSpecification[] = []): Promise<ModelHunk> {
        return loadInner(this.candyApiDraftClient, this.formSettings.useServerSideFLangNormalization, loadingPathSpecs);
    }

    private markOptionalSectionsAsIgnoredByEvents(updateEvents: readonly ModelEvent[]) {
        const removedChildrenQuery = Iterator.from(updateEvents)
            .filter(x => x.name === "children")
            .map(x => {
                const value: string[] = x.value ?? [];
                const oldValue: string[] = x.oldValue ?? [];
                const valueSet = new Set<string>(value ?? []);
                const removedValues = oldValue.filter(v => !valueSet.has(v));
                return removedValues.map(rv => x.path.joinWith(rv));
            })
            .flatMap(x => x);
        this.sections.markSectionsAsIgnoredByPrefix(removedChildrenQuery);
    }

    private createModel(): IModel {
        return new Model(this.modelOverStore, this.modelNormalizer, this.useServerSideCalculations);
    }

    private beginCollectChangeSet() {
        const changeSet = new ChangeSet(this.useServerSideCalculations);
        const action: EventsSubscriptionAction = events => {
            for (const event of events) {
                changeSet.appendEventInfo(event);
            }
        };
        this.eventsTrackers.add(action);
        return {
            releaseAndGetChangeSet: () => {
                this.eventsTrackers.delete(action);
                return changeSet;
            },
        };
    }
}

const onlyValidationFunctionsPredicate = (fn: ICalculationFunction) =>
    fn instanceof LegacyValidationFunctionAdapter ||
    fn instanceof AggregatedValidationFunction ||
    fn instanceof ValidationFunction;

const onlyNonValidationFunctionsPredicate = (fn: ICalculationFunction) => !onlyValidationFunctionsPredicate(fn);

function updateAutoFlags(
    model: IModel,
    dataDeclarationHelper: DataDeclarationHelper,
    events: Iterable<ModelEvent>
): void {
    const paths = AbsoluteModelPathSet.unique(
        Iterator.from(events)
            .filter(isChangeModelEventInfo)
            .map(x => x.path)
    );
    for (const path of paths) {
        if (dataDeclarationHelper.isNodeHasAutoFlagEntry(path)) {
            const type = dataDeclarationHelper.getType(path);
            const needCastToDecimal = type === "decimal" || type === "integer";
            const value = model.getViewModelField(path, "value");
            const autoValue = model.getViewModelField(path, "autoValue");
            model.setViewModelField(path, "autoFlag", isValueEqualsAutoValue(value, autoValue, needCastToDecimal));
        }
    }
}

function isValueEqualsAutoValue(value: unknown, autoValue: unknown, needCastToDecimal: boolean): boolean {
    if (needCastToDecimal && typeof value === "string" && typeof autoValue === "string") {
        const valueAsBig = wrapDecimalOrUndefined(value);
        const autoValueAsBig = wrapDecimalOrUndefined(autoValue);
        if (valueAsBig != undefined && autoValueAsBig != undefined) {
            return valueAsBig.eq(autoValueAsBig);
        }
    }
    return value === autoValue;
}

function* splitModelPathIfErrorCountRequired(
    modelPath: AbsoluteResolvedModelPath,
    dataDeclarationHelper: DataDeclarationHelper
): Iterable<AbsoluteResolvedModelPath> {
    const tokens = modelPath.getPathPartsAsArray();
    for (let i = 0; i < tokens.length; i++) {
        const currentModelPath = createAbsoluteResolvedFromTokens(tokens.slice(0, i));
        const currentToken = tokens[i];
        const hasChildren = dataDeclarationHelper.isNodeHasChildrenEntry(currentModelPath);
        const needsCount = dataDeclarationHelper.isErrorsAndWarningsCountRequired(currentModelPath);
        if (currentToken != undefined) {
            if (hasChildren || needsCount) {
                yield currentModelPath;
                if (hasChildren) {
                    yield currentModelPath.joinWith(currentToken);
                }
            }
        }
    }
}

function updateErrorCount(
    sections: Sections,
    dataDeclarationHelper: DataDeclarationHelper,
    formRoot: AbsoluteResolvedModelPath,
    model: IModel,
    events: Iterable<ModelEvent>
): void {
    function isErrorListEmpty(list: undefined | string[]): boolean {
        return list == undefined || list.length === 0;
    }

    const warningValue: number | string | undefined = model.getViewModelField(formRoot, "warningsCount");
    const warningCount = wrapDecimalOperand(warningValue);
    const errorValue: number | string | undefined = model.getViewModelField(formRoot, "errorsCount");
    const errorCount = wrapDecimalOperand(errorValue);

    const getEventDiff = (event: ModelEvent) => {
        const prevValue = event.oldValue;
        const nextValue = event.value;
        if (event.type === "remove") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (!isErrorListEmpty(prevValue)) {
                return -1;
            }
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (isErrorListEmpty(nextValue) && !isErrorListEmpty(prevValue)) {
                return -1;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (!isErrorListEmpty(nextValue) && isErrorListEmpty(prevValue)) {
                return 1;
            }
        }
        return 0;
    };

    const getPathToUpdate = (modelPath: AbsoluteResolvedModelPath): Iterable<AbsoluteResolvedModelPath> =>
        splitModelPathIfErrorCountRequired(modelPath, dataDeclarationHelper);

    const isPathIgnored = (pathToUpdate: AbsoluteModelPath) => {
        if (PathTokens.isInstanceToken(IterUtils.last(pathToUpdate.getPathParts()))) {
            return sections.isIgnoredPathByMasks(pathToUpdate.getParentPath());
        }
        return sections.isIgnoredPathByMasks(pathToUpdate);
    };

    let errorsDiff = 0;
    let warningsDiff = 0;
    for (const event of events) {
        const targetCountFieldName =
            event.name === "error" ? "errorsCount" : event.name === "warning" ? "warningsCount" : undefined;
        if (targetCountFieldName != undefined) {
            const diff = getEventDiff(event);
            if (diff !== 0) {
                for (const pathToUpdate of getPathToUpdate(event.path)) {
                    const currentValue: number | string | undefined = model.getViewModelField(
                        pathToUpdate,
                        targetCountFieldName
                    );
                    if (!isPathIgnored(pathToUpdate)) {
                        // note: кейс очистки раздела
                        if (currentValue == undefined && diff < 0) {
                            continue;
                        }

                        model.setViewModelField(
                            pathToUpdate,
                            targetCountFieldName,
                            wrapDecimalOperand(currentValue).add(diff).toNumber()
                        );
                    }
                }
                errorsDiff += targetCountFieldName === "errorsCount" ? diff : 0;
                warningsDiff += targetCountFieldName === "warningsCount" ? diff : 0;
            }
        }
    }

    const nextErrorCount = errorCount.add(errorsDiff);
    const nextWarningCount = warningCount.add(warningsDiff);
    model.setViewModelField(formRoot, "errorsCount", nextErrorCount.toNumber());
    // Такая фигня, только чтобы проходили аппрувал тесты. Убрать и сетить безустловно
    if (!warningCount.eq(0) || !nextWarningCount.eq(0)) {
        model.setViewModelField(formRoot, "warningsCount", nextWarningCount.toNumber());
    }
}
